import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BuildingLibraryIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  HeartIcon,
  LockClosedIcon,
  PresentationChartLineIcon,
  SparklesIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  StarIcon,
} from '@heroicons/react/24/solid';
import Footer from '../components/common/Footer';
import Navigation from '../components/common/Navigation';
import ScrollToTop from '../components/common/ScrollToTop';

const HERO_IMAGE_FILE = 'oakvale1.webp';
const heroImageSrc = encodeURI(
  `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/${HERO_IMAGE_FILE}`.replace(/^\/{2,}/, '/')
);

const testimonials = [
  {
    name: 'Sarah Johnson',
    role: 'Small business owner',
    content:
      'Oakvale worked with us on a line of credit when we were ready to grow. Real people, clear answers, and no runaround.',
  },
  {
    name: 'Michael Chen',
    role: 'First-time homebuyer',
    content:
      'The mortgage team walked us through every step. We always knew what to expect and what came next.',
  },
  {
    name: 'Emily Rodriguez',
    role: 'Member since 2019',
    content:
      'I use mobile banking every week. Transfers are simple, and when I need help, someone local picks up the phone.',
  },
];

const services = [
  {
    name: 'Personal banking',
    description: 'Everyday accounts and tools built around how you actually live and spend.',
    features: ['Free checking options', 'Competitive savings', 'Digital & mobile banking'],
    Icon: BuildingLibraryIcon,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-800',
    ring: 'ring-emerald-200/80',
  },
  {
    name: 'Loans & lending',
    description: 'Auto, home, and personal loans with straightforward terms and local decisions.',
    features: ['Mortgages & refi support', 'Auto & personal loans', 'Business lending'],
    Icon: ChartBarIcon,
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-800',
    ring: 'ring-teal-200/80',
  },
  {
    name: 'Guidance & planning',
    description: 'Education and planning resources so you can make confident money decisions.',
    features: ['Financial wellness sessions', 'Retirement planning', 'Investment referrals'],
    Icon: PresentationChartLineIcon,
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-800',
    ring: 'ring-sky-200/80',
  },
];

const highlights = [
  {
    title: 'Bank on your schedule',
    description: 'Check balances, move money, pay bills, and deposit checks from your phone—securely.',
    Icon: DevicePhoneMobileIcon,
    stat: '24/7 digital access',
  },
  {
    title: 'Decisions stay local',
    description: 'Lending and support are rooted in the community we share with our members.',
    Icon: HeartIcon,
    stat: 'Member-owned',
  },
  {
    title: 'Learn as you grow',
    description: 'Workshops and one-on-one conversations to help you build stronger financial habits.',
    Icon: AcademicCapIcon,
    stat: 'Free resources',
  },
];

const quickLinks = [
  {
    title: 'Online banking',
    description: 'Secure access to your accounts anytime.',
    Icon: LockClosedIcon,
    to: '/login',
  },
  {
    title: 'Accounts & loans',
    description: 'Explore checking, savings, and borrowing options.',
    Icon: BuildingLibraryIcon,
    to: '/products',
  },
  {
    title: 'Talk with us',
    description: 'Questions about membership or an application?',
    Icon: ChatBubbleLeftRightIcon,
    to: '/contact',
  },
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <Navigation />

      {/* Hero */}
      <header className="relative overflow-hidden bg-gradient-to-br from-[#0c1f18] via-emerald-950 to-[#042f2e]">
        <div className="pointer-events-none absolute inset-0 bg-pattern-dots opacity-[0.07]" />
        <div className="pointer-events-none absolute -right-24 top-0 h-[520px] w-[520px] rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-teal-400/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-24 sm:px-6 sm:pb-24 sm:pt-24 lg:px-8 lg:pb-28 lg:pt-28">
          <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-6">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-emerald-100/95 backdrop-blur-sm">
                <ShieldCheckIcon className="h-4 w-4 text-emerald-300" aria-hidden />
                <span>Federally insured by NCUA · Member-owned</span>
              </div>
              <h1 className="font-serif text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
                Banking that belongs{' '}
                <span className="bg-gradient-to-r from-emerald-200 via-teal-200 to-cyan-200 bg-clip-text text-transparent">
                  to you
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-stone-200/90 sm:text-xl">
                Oakvale Credit Union is a not-for-profit cooperative: profits return to members as better rates,
                lower fees, and investments in the places we call home.
              </p>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-emerald-900/30 transition hover:from-emerald-400 hover:to-teal-500 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 focus:ring-offset-emerald-950"
                >
                  Become a member
                  <ArrowRightIcon className="ml-2 h-5 w-5" aria-hidden />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-2xl border border-white/20 bg-white/5 px-8 py-3.5 text-base font-semibold text-white backdrop-blur-sm transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-emerald-950"
                >
                  Member sign in
                </Link>
              </div>
              <dl className="mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-10 text-center sm:text-left">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-emerald-200/70">Members</dt>
                  <dd className="mt-1 font-serif text-2xl font-bold text-white">50k+</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-emerald-200/70">Serving since</dt>
                  <dd className="mt-1 font-serif text-2xl font-bold text-white">1973</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wider text-emerald-200/70">NCUA insured</dt>
                  <dd className="mt-1 font-serif text-2xl font-bold text-white">$250k</dd>
                </div>
              </dl>
            </div>

            <div className="lg:col-span-6">
              <div className="relative mx-auto max-w-lg lg:mx-0 lg:max-w-none">
                <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-emerald-400/30 via-teal-500/20 to-cyan-500/20 blur-2xl" />
                <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.07] p-8 shadow-2xl backdrop-blur-md sm:p-10">
                  <div className="grid gap-8 sm:grid-cols-2 sm:items-start">
                    <img
                      src={heroImageSrc}
                      alt="Oakvale Credit Union"
                      className="h-auto w-full max-w-[360px] rounded-2xl border border-white/10 object-cover shadow-lg sm:max-w-none"
                      width={720}
                      height={720}
                      loading="eager"
                    />
                    <div className="w-full space-y-4 text-left">
                      <p className="text-sm font-semibold uppercase tracking-wide text-emerald-100/80">
                        Why members choose Oakvale
                      </p>
                      <ul className="w-full space-y-3 text-sm leading-relaxed text-stone-100/90">
                        <li className="flex items-start gap-3">
                          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" aria-hidden />
                          <span>Competitive rates on loans and deposits</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" aria-hidden />
                          <span>No shareholders—members come first</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" aria-hidden />
                          <span>Local decisions and real people to talk to</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-8 grid gap-3 rounded-2xl bg-black/20 p-4 sm:grid-cols-2">
                    <div className="rounded-xl bg-white/5 px-4 py-3">
                      <p className="text-xs text-emerald-100/70">Featured</p>
                      <p className="mt-1 text-sm font-semibold text-white">High-yield savings</p>
                      <p className="text-xs text-stone-300">Grow your balance with a competitive APY.</p>
                    </div>
                    <div className="rounded-xl bg-white/5 px-4 py-3">
                      <p className="text-xs text-emerald-100/70">Featured</p>
                      <p className="mt-1 text-sm font-semibold text-white">Auto loans</p>
                      <p className="text-xs text-stone-300">Pre-qualify online—same-day decisions when possible.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Quick links */}
      <section className="relative z-10 -mt-10 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-4 md:grid-cols-3 md:gap-6">
            {quickLinks.map(({ title, description, Icon, to }) => (
              <Link
                key={title}
                to={to}
                className="group flex gap-4 rounded-2xl border border-stone-200/80 bg-white p-6 shadow-sm ring-1 ring-stone-900/5 transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-800 transition group-hover:bg-emerald-100">
                  <Icon className="h-6 w-6" aria-hidden />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-stone-900">{title}</h2>
                  <p className="mt-1 text-sm leading-relaxed text-stone-600">{description}</p>
                  <span className="mt-3 inline-flex items-center text-sm font-medium text-emerald-700">
                    Go
                    <ArrowRightIcon className="ml-1 h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-emerald-700">Membership benefits</p>
            <h2 className="mt-3 font-serif text-3xl font-bold text-stone-900 sm:text-4xl">Everything you need, in one relationship</h2>
            <p className="mt-4 text-lg text-stone-600">
              From your first checking account to your biggest financial goals—we are built to grow with you.
            </p>
          </div>

          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {services.map(({ name, description, features, Icon, iconBg, iconColor, ring }) => (
              <article
                key={name}
                className={`relative flex flex-col rounded-3xl border border-stone-200 bg-white p-8 shadow-sm ring-1 ${ring} transition hover:-translate-y-1 hover:shadow-lg`}
              >
                <div className={`mb-6 inline-flex rounded-2xl p-3 ${iconBg}`}>
                  <Icon className={`h-8 w-8 ${iconColor}`} aria-hidden />
                </div>
                <h3 className="font-serif text-xl font-bold text-stone-900">{name}</h3>
                <p className="mt-2 text-stone-600">{description}</p>
                <ul className="mt-6 flex-1 space-y-3">
                  {features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-stone-700">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/products"
                  className="mt-8 inline-flex items-center text-sm font-semibold text-emerald-800 hover:text-emerald-900"
                >
                  View products
                  <ArrowRightIcon className="ml-1 h-4 w-4" aria-hidden />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0a1814] via-emerald-950 to-teal-950 py-20 sm:py-24">
        <div className="pointer-events-none absolute inset-0 bg-pattern-dots opacity-[0.06]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-200">
              <SparklesIcon className="h-6 w-6" aria-hidden />
            </div>
            <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">Built for real life</h2>
            <p className="mt-4 text-lg text-emerald-100/85">
              Technology and people working together—so you spend less time banking and more time living.
            </p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {highlights.map(({ title, description, Icon, stat }) => (
              <div
                key={title}
                className="rounded-3xl border border-white/10 bg-white/[0.06] p-8 backdrop-blur-sm transition hover:border-emerald-300/30 hover:bg-white/[0.1]"
              >
                <Icon className="h-9 w-9 text-emerald-200" aria-hidden />
                <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-emerald-200/80">{stat}</p>
                <h3 className="mt-2 font-serif text-xl font-bold text-white">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-emerald-50/85">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-serif text-3xl font-bold text-stone-900 sm:text-4xl">Trusted by our community</h2>
            <p className="mt-4 text-lg text-stone-600">
              Hear from members who have shaped their financial lives with Oakvale Credit Union.
            </p>
          </div>

          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {testimonials.map((t) => (
              <blockquote
                key={t.name}
                className="flex flex-col rounded-3xl border border-stone-200 bg-white p-8 shadow-sm ring-1 ring-stone-900/5"
              >
                <p className="text-lg leading-relaxed text-stone-700">&ldquo;{t.content}&rdquo;</p>
                <footer className="mt-8 flex items-center justify-between gap-4 border-t border-stone-100 pt-6">
                  <div>
                    <cite className="not-italic font-semibold text-stone-900">{t.name}</cite>
                    <p className="text-sm text-stone-500">{t.role}</p>
                  </div>
                  <div className="flex gap-0.5" aria-label="5 out of 5 stars">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <StarIcon key={i} className="h-5 w-5 text-amber-400" aria-hidden />
                    ))}
                  </div>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Community */}
      <section className="border-y border-stone-200 bg-stone-100/60 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-emerald-800">Community</p>
              <h2 className="mt-3 font-serif text-3xl font-bold text-stone-900 sm:text-4xl">We invest where our members live and work</h2>
              <p className="mt-5 text-lg leading-relaxed text-stone-600">
                At Oakvale Credit Union, giving back is part of our structure—not a marketing slogan. Sponsorships,
                financial education, and volunteer time help strengthen the neighborhoods we share.
              </p>
              <div className="mt-10 grid grid-cols-2 gap-4 sm:max-w-md">
                <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                  <p className="font-serif text-3xl font-bold text-emerald-800">$5M+</p>
                  <p className="mt-1 text-sm text-stone-600">Community support &amp; sponsorships</p>
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                  <p className="font-serif text-3xl font-bold text-teal-800">10k+</p>
                  <p className="mt-1 text-sm text-stone-600">Volunteer hours from our team</p>
                </div>
              </div>
              <Link
                to="/about"
                className="group mt-10 inline-flex items-center font-semibold text-emerald-800 hover:text-emerald-900"
              >
                Our story &amp; mission
                <ArrowRightIcon className="ml-2 h-5 w-5 transition group-hover:translate-x-1" aria-hidden />
              </Link>
            </div>
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-gradient-to-tr from-emerald-400/25 to-teal-500/20 blur-2xl" />
              <img
                src="/images/community.webp"
                alt="Oakvale Credit Union volunteers and members at a local community event"
                className="relative w-full rounded-3xl border border-stone-200/80 object-cover shadow-xl"
                width={800}
                height={600}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-to-r from-emerald-700 via-teal-700 to-emerald-800 py-20 sm:py-24">
        <div className="pointer-events-none absolute inset-0 bg-pattern-dots opacity-[0.08]" />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold text-white sm:text-4xl">Ready to join Oakvale?</h2>
          <p className="mt-5 text-lg text-emerald-50/95">
            Open your membership online in minutes, or reach out—we will help you find the right accounts and loans
            for your goals.
          </p>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center rounded-2xl bg-white px-8 py-3.5 text-base font-semibold text-emerald-900 shadow-lg transition hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-emerald-800"
            >
              Start membership
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-2xl border-2 border-white/80 bg-transparent px-8 py-3.5 text-base font-semibold text-white transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-2 focus:ring-offset-emerald-800"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default HomePage;
